<script setup lang="ts">
    import { onMounted, inject, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';
    import Loading from '@shared/components/Loading.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import SubscriptionAddons from '@backoffice/SubscriptionAddons/SubscriptionAddons.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { AccountBalance, SubscriptionAccess, getSubscriptionStatusClass } from '@views/BackOffice/Subscription/Subscription.access';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { getAccountStatusClass } from '@cwd/Views/BackOffice/Account/Account.access';
    import { getInvoices, AccountInvoice } from '@account/AccountInvoices/AccountInvoices.access';
    import InvoicesTable from '@account/AccountInvoices/InvoicesTable.vue';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import SubscriptionTab from './components/SubscriptionTab.vue';
    import CompanyTab from './components/CompanyTab.vue';
    import BillingTab from './components/BillingTab.vue';
    import CreateAccount from './components/CreateAccount.vue'

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const props = defineProps<{
        serviceSubscriptionId: string;
        isModal: boolean;
    }>();

    const state = reactive<{
        invoices: AccountInvoice[] | null;
        selectedBalance: AccountBalance | null;
        balanceAmount: number;
        balanceCurrecyCode: string;
        loadingInvoices: boolean;    
        showCreateAccountForm: boolean;
    }>({
        invoices: null,
        selectedBalance: null,
        balanceAmount: 0,
        balanceCurrecyCode: 'SEK',
        loadingInvoices: false,     
        showCreateAccountForm: false,
    });

    onMounted(async () => {
        state.loadingInvoices = true;
        
        await Promise.all([
            subscriptionAccess.getServiceSubscription(props.serviceSubscriptionId).then(async () => {
                await Promise.all([
                    subscriptionAccess.getClientCompany(subscriptionAccess.state.boServiceSubscription!.ClientCompanyId),
                    subscriptionAccess.getClientCompanyAccounts(),
                    subscriptionAccess.getOrders(),
                    subscriptionAccess.getMonthlyPriceWithAddons(),
                    subscriptionAccess.getMonthlyPriceBreakdown(),
                    subscriptionAccess.getRenewalExclusionReason(),
                ]);
            }),
            subscriptionAccess.getAccountBalances(props.serviceSubscriptionId),
            getInvoices(props.serviceSubscriptionId, true)
                .then((invoices) => {
                    state.invoices = invoices;
                })
                .finally(() => {
                    state.loadingInvoices = false;
                }),
            subscriptionAccess.getOpenAIConsumption(props.serviceSubscriptionId),
        ]);
    });

    function selectAccountBalance(balance: AccountBalance) {
        state.selectedBalance = balance;
    }

    async function giveAwayMoney() {
        await subscriptionAccess.giveAwayMoney(state.selectedBalance!, state.balanceAmount, state.balanceCurrecyCode).then(() => {
            state.balanceAmount = 0;
            state.selectedBalance = null;
            });
        }

    function handleAccountCreated() {
        state.showCreateAccountForm = false;
    }

    function handleCancelled() {
        state.showCreateAccountForm = false;
    }
</script>

<template>
    <div v-if="featureAccess.hasBackOfficeAccess()">
        <div v-if="!props.isModal">
            <p class="fw-600 m-lg-t m-xs-b">
                <router-link :to="{ name: 'SubscriptionList' }">
                    <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
                    Subscriptions
                </router-link>
            </p>
        </div>

        <Loading
            :show="subscriptionAccess.state.loadingBOClientCompany || subscriptionAccess.state.loadingBOServiceSubscription"
            loadingStyle="progress-bar"></Loading>

        <template v-if="subscriptionAccess.state.boServiceSubscription != null && subscriptionAccess.state.boClientCompany != null">
            <h3>
                <i class="fa fa-square" aria-hidden="true" :class="getSubscriptionStatusClass(subscriptionAccess.state.boServiceSubscription)"></i>
                {{ subscriptionAccess.state.boClientCompany.Name }}
            </h3>

            <p>
                {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceSubscriptionTier.Name }}
                {{ formatNumber(subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.ServiceLevel.ContactLimit) }}

                <span v-if="!subscriptionAccess.state.loadingMonthlyPriceWithAddons">
                    | {{ formatNumber(subscriptionAccess.state.monthlyPriceWithAddons) }}
                    {{ subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode }}
                </span>
            </p>

            <CopyButton
                buttonText="Copy service subscription id"
                :textToCopy="subscriptionAccess.state.boServiceSubscription.ServiceSubscriptionId"
                :tooltip="subscriptionAccess.state.boServiceSubscription.ServiceSubscriptionId" />
            <CopyButton
                class="m-xs-l"
                buttonText="Copy client company id"
                :textToCopy="subscriptionAccess.state.boServiceSubscription.ClientCompanyId"
                :tooltip="subscriptionAccess.state.boServiceSubscription.ClientCompanyId" />

            <tabs navClass="tabs list-unstyled">
                <tab title="Accounts">
                    <Loading :show="subscriptionAccess.state.loadingAccounts" loadingStyle="progress-bar"></Loading>
                    
                    <table v-if="!subscriptionAccess.state.loadingAccounts" class="table table-hover">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Account</th>
                                <th>Created</th>
                                <th>Contact count</th>
                                <th>User count</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in subscriptionAccess.state.accounts">
                                <td class="autofit text-nowrap">{{ index + 1 }}.</td>
                                <td>
                                    <i class="fa fa-square m-xs-r" aria-hidden="true" :class="getAccountStatusClass(row)"></i>
                                    <router-link :to="{ name: 'AccountEdit', params: { accountId: row.AccountId } }">
                                        {{ row.Name }}
                                    </router-link>
                                </td>
                                <td>
                                    <time :datetime="row.Created">{{ formatDateTime(row.Created) }}</time>
                                </td>
                                <td>
                                    {{ formatNumber(row.ContactCount) }}
                                </td>
                                <td>
                                    {{ formatNumber(row.UserCount) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <div class="form-group" v-if="!subscriptionAccess.state.loadingAccounts && !state.showCreateAccountForm">
                        <button type="button" class="btn btn-default" @click="() => { state.showCreateAccountForm = true; }">
                            Create account
                        </button>
                    </div>

                    <CreateAccount 
                        v-if="state.showCreateAccountForm" 
                        @cancelled="handleCancelled"
                        :onComplete="handleAccountCreated" />
                </tab>
                <tab title="Subscription">
                    <SubscriptionTab :service-subscription-id="props.serviceSubscriptionId" />
                </tab>
                <tab title="Company">
                    <CompanyTab />
                </tab>
                <tab title="Features & addons">
                    <SubscriptionAddons :serviceSubscriptionId="props.serviceSubscriptionId"></SubscriptionAddons>
                </tab>
                <tab title="Account balances">
                    <h3>Account balances</h3>
                    <Loading :show="subscriptionAccess.state.loadingAccountBalances" loadingStyle="progress-bar"></Loading>

                    <template v-if="!subscriptionAccess.state.loadingAccountBalances">
                        <div v-if="state.selectedBalance" class="fieldset-grid">
                            <h4>Selected: {{ state.selectedBalance.Name }}</h4>

                            <fieldset>
                                <div class="form-group">
                                    <label for="balanceAmount">Amount:</label>
                                    <input id="balanceAmount" type="number" class="form-control" v-model="state.balanceAmount" min="0" />
                                </div>
                                <div class="form-group">
                                    <label for="balanceCurrencyCode">Currency:</label>
                                    <select id="balanceCurrencyCode" class="form-control" v-model="state.balanceCurrecyCode">
                                        <option v-for="currency in subscriptionAccess.state.currencyCodes" :key="currency" :value="currency">
                                            {{ currency }}
                                        </option>
                                    </select>
                                </div>
                            </fieldset>

                            <div class="form-group">
                                <button class="btn btn-primary" :disabled="state.balanceAmount <= 0" @click="giveAwayMoney()">Make it rain!</button>
                            </div>
                        </div>

                        <img v-if="subscriptionAccess.state.gaveAwayMoney && !state.selectedBalance" class="img-responsive mir" :src="'/Content/img/mir.gif'" />

                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Account</th>
                                    <th>Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(balance, index) in subscriptionAccess.state.accountBalances">
                                    <td>{{ index + 1 }}.</td>
                                    <td>
                                        <template v-if="state.selectedBalance == balance">
                                            <template v-if="balance.Name">{{ balance.Name }}</template>
                                            <template v-else>Subscription</template>
                                        </template>
                                        <a v-else href="#" @click="selectAccountBalance(balance)">
                                            <template v-if="balance.Name">{{ balance.Name }}</template>
                                            <template v-else>Subscription</template>
                                        </a>
                                    </td>
                                    <td>
                                        <template v-if="balance.Balance == null || balance.CurrencyCode == null">0</template>
                                        <template v-else>{{ formatMoney(balance.Balance, balance.CurrencyCode) }}</template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </tab>
                <tab title="Transactions">
                    <Loading :show="subscriptionAccess.state.loadingOrders" loadingStyle="progress-bar"></Loading>

                    <table v-if="!subscriptionAccess.state.loadingOrders" class="table table-hover">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Created</th>
                                <th>Status</th>
                                <th>Type</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(order, index) in subscriptionAccess.state.orders">
                                <td>{{ index + 1 }}.</td>
                                <td>{{ formatDateTime(order.Created) }}</td>
                                    <td>
                                    <i
                                        v-if="order.OrderStatus"
                                        class="fa fa-square"
                                        aria-hidden="true"
                                        :class="{ 'status-ok': order.OrderStatus == 'Paid', 'status-not-ok': order.OrderStatus != 'Paid' }"></i>
                                    <span>{{ order.OrderStatus }}</span>
                                    </td>
                                <td>{{ order.OrderType }}</td>
                                <td>{{ formatMoney(order.Amount, order.CurrencyCode) }}</td>
                                </tr>
                            </tbody>
                        </table>
                </tab>
                <tab title="Invoices">
                    <h2 class="main-title">{{ $Resources.InvoicesTab }}</h2>

                    <Loading :show="state.loadingInvoices" loadingStyle="progress-bar"></Loading>

                    <InvoicesTable
                        v-if="!state.loadingInvoices"
                        :invoices="state.invoices"
                        :serviceSubscriptionId="props.serviceSubscriptionId"></InvoicesTable>
                </tab>
                <tab title="Billing info">
                    <BillingTab />
                </tab>
            </tabs>
        </template>
    </div>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
